import { Edit3 } from "@tamagui/lucide-icons";
import { Timestamp } from "firebase/firestore";
import { observer } from "mobx-react-lite";
import React from "react";
import { View, XStack, YStack } from "tamagui";
import { DatePicker } from "../../core/components/FormDataModal";
import { PrimaryCheckText } from "../../core/components/PrimaryCheck";
import { formatDate } from "../../core/dayJs";
import {
  HelperType,
  type HelpItem,
  HelpItemRequiredType,
} from "../../helper/HelperManager";
import { useHelper } from "../../helper/useHelper";
import { Box } from "../../ui/Box";
import { Checkbox } from "../../ui/Checkbox";
import { ModuleEditor } from "../../ui/editor/useModuleEditor";
import { Label } from "../../ui/Label";
import { Subtext } from "../../ui/Subtext";
import { UserDisplay } from "../../ui/UserAvatar";
import type UserModel from "../../users/UserModel";
import { DueDateModal } from "../shared/DueDateModal";
import { DateDue } from "./LateDate";
import type DateDueModuleModel from "./ReminderModuleModel";
import type ReminderModuleModel from "./ReminderModuleModel";

const UserRow: React.FC<{ module: ReminderModuleModel; user: UserModel }> = ({
  user,
  module,
}) => {
  const excluded = module.data.excludedUsers.includes(user.uid);

  return (
    <XStack
      key={user.uid}
      opacity={excluded ? 0.5 : 1}
      width={"100%"}
      paddingHorizontal={"$larger"}
      paddingVertical={"$medium"}
      alignItems="center"
      justifyContent="space-between"
      onPress={() => module.toggleCompletedByUser(user.uid)}
      borderTopColor={"$divider"}
      borderTopWidth={1}
    >
      <UserDisplay userUid={user.uid} />
      <XStack alignItems="center" gap="$large">
        {module.data.completedBy.includes(user.uid) ? undefined : (
          <Subtext
            cursor="pointer"
            onPress={(e) => {
              e.stopPropagation();
              module.toggleExcluded(user.uid);
            }}
            textDecorationLine="underline"
          >
            {excluded ? "Include" : "Exclude"}
          </Subtext>
        )}
        {!excluded && (
          <Checkbox checked={module.data.completedBy.includes(user.uid)} />
        )}
      </XStack>
    </XStack>
  );
};

export const ReminderUI = observer(
  ({ module }: { module: DateDueModuleModel }) => {
    const [showDateDuePicker, setShowDateDuePicker] = React.useState(false);

    const items: HelpItem[] = React.useMemo(
      () => [
        {
          type: HelpItemRequiredType.Required,
          label: "Add a description below",
          isCompleted: () => {
            return module.hasDescription;
          },
        },
        {
          type: HelpItemRequiredType.Tip,
          label: "Register your email to get reminders",
          isEnabled: () => {
            return !module.plan.userInPlan?.data.email;
          },
          isCompleted: () => {
            return false;
          },
        },
      ],
      [module.hasDescription, module.plan.userInPlan?.data.email],
    );

    const helper = useHelper(module.uid, HelperType.DateDue, items);

    return (
      <YStack width="100%" gap="$space.medium">
        {helper}

        <View>
          <Box>
            <YStack
              flex={1}
              width="100%"
              marginTop="$space.small"
              gap={"$space.large"}
            >
              <YStack gap="$small">
                {module.isCompleted ? (
                  <PrimaryCheckText label text={"Completed"} />
                ) : (
                  <>
                    <XStack flex={1} gap={"$space.small"} alignItems="center">
                      <DateDue dateDue={module.data.dateDue} />

                      <XStack
                        gap="$space.small"
                        alignItems="center"
                        onPress={() => {
                          setShowDateDuePicker(true);
                        }}
                        cursor="pointer"
                      >
                        <Edit3 size={16} />
                        <Subtext>Change</Subtext>
                      </XStack>
                      <DatePicker
                        value={
                          module.data.dateDue
                            .toDate()
                            .toISOString()
                            .split("T")[0]
                        }
                        open={showDateDuePicker}
                        onOpenChange={(open: boolean) => {
                          setShowDateDuePicker(open);
                        }}
                        sheetId="dateDuePicker"
                        onSelect={(date) => {
                          module.updateDate(Timestamp.fromDate(new Date(date)));
                          setShowDateDuePicker(false);
                        }}
                      />
                    </XStack>
                    <Subtext>
                      {formatDate(module.data.dateDue.toDate().toISOString())}
                    </Subtext>
                  </>
                )}
              </YStack>

              <ModuleEditor module={module} />
            </YStack>
          </Box>
          <Box noPadding borderTopWidth={1} borderTopColor={"$divider"}>
            <View>
              <XStack
                width={"100%"}
                paddingHorizontal={"$larger"}
                paddingVertical={"$medium"}
                alignItems="center"
                justifyContent="space-between"
                onPress={() => module.toggleEveryoneCompleted()}
              >
                <Label>Completed by everyone</Label>
                <Checkbox
                  checked={module.data.completedBy.length > 0}
                  semiChecked={
                    module.data.completedBy.length !== module.activeUsers.length
                  }
                />
              </XStack>
              {module.plan.users
                .sort((a, b) => {
                  const aExcluded = module.data.excludedUsers.includes(a.uid);
                  const bExcluded = module.data.excludedUsers.includes(b.uid);
                  if (aExcluded !== bExcluded) {
                    return aExcluded ? 1 : -1;
                  }

                  return a.data.displayName.localeCompare(b.data.displayName);
                })
                .map((user) => (
                  <UserRow key={user.uid} user={user} module={module} />
                ))}
            </View>
          </Box>
        </View>

        {module.showFormSheet && (
          <DueDateModal
            initialDueDate={module.data.dateDue}
            visible={module.showFormSheet}
            module={module}
            onSave={async ({ dueDate }) => {
              void module.updateDate(dueDate);
            }}
            onClose={() => module.toggleFormSheet()}
          />
        )}
      </YStack>
    );
  },
);
